import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import type { Experience } from '@/lib/types/supabase';

interface ExperienceState {
  experiences: Experience[];
  loading: boolean;
  error: string | null;
  
  loadExperiences: (force?: boolean) => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const CACHE_TTL = 15 * 60 * 1000; // 15 minutes
const cache: {
  experiences?: CacheEntry<Experience[]>;
} = {};

const isCacheValid = <T>(entry?: CacheEntry<T>) => {
  return entry && (Date.now() - entry.timestamp) < CACHE_TTL;
};

export const useExperienceStore = create<ExperienceState>((set, _get) => ({
  experiences: [],
  loading: false,
  error: null,

  loadExperiences: async (force = false) => {
    try {
      if (!force && isCacheValid(cache.experiences)) {
        set({ experiences: cache.experiences!.data, loading: false });
        return;
      }

      set({ loading: true, error: null });
      
      const { data, error } = await supabase
        .from('experiences')
        .select(`
          *,
          creator:creator_id (
            id,
            full_name,
            bio,
            profile_image,
            creator_profiles(*)
          ),
          category:categories(*),
          media:experience_media (
            url,
            type,
            order_index
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Process the data to unify creator_profile/creator_profiles naming
      const processedData = data?.map(exp => {
        if (exp.creator && Array.isArray(exp.creator.creator_profiles) && exp.creator.creator_profiles.length > 0) {
          return {
            ...exp,
            creator: {
              ...exp.creator,
              creator_profile: exp.creator.creator_profiles[0] || null
            }
          };
        }
        return exp;
      }) || [];

      cache.experiences = {
        data: processedData,
        timestamp: Date.now()
      };

      set({ 
        experiences: processedData,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Failed to load experiences:', error);
      set({ 
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load experiences',
        experiences: cache.experiences?.data || []
      });
    }
  },

  clearError: () => set({ error: null }),
  setLoading: (loading: boolean) => set({ loading })
})); 